import { useLocation, useParams } from "react-router-dom";
import { skipToken } from "@reduxjs/toolkit/query";

import EditAdmin from "../Admin/EditAdmin";
import EditNurse from "../Nurse/EditNurse";
import EditCourier from "../Courier/EditCourier";
import { useGetUserDetailsQuery, useGetUserRolesQuery } from "../../services/api";
import { resolveUserTypeFromRoleName } from "./userShared";

/** Supported user type values for edit-user routing. */
type SupportedUserType = "NURSE" | "ADMIN" | "DOCTOR" | "COURIER";

/** Route state shape for edit-user navigation. */
type EditUserState = {
  userType?: SupportedUserType;
};

/**
 * Type guard for the edit-user route state.
 *
 * Args:
 *   value: Unknown location state to validate.
 *
 * Returns:
 *   boolean: True when the state matches the expected shape.
 */
const isEditUserState = (value: unknown): value is EditUserState => {
  if (typeof value !== "object" || value === null) {
    return false;
  }
  const maybeState = value as { userType?: unknown };
  const userType = maybeState.userType;
  return (
    userType === undefined ||
    userType === "NURSE" ||
    userType === "ADMIN" ||
    userType === "DOCTOR" ||
    userType === "COURIER"
  );
};

/**
 * Routes to the correct edit form based on the selected user tab.
 *
 * Returns:
 *   JSX.Element: The edit user form for the resolved user type.
 *
 * Notes:
 *   Attempts to resolve user type from API data when the route state is missing.
 *   Returns null while resolving user type to avoid rendering an incorrect view.
 */
const EditUser = () => {
  const { id } = useParams();
  const location = useLocation();
  const state = isEditUserState(location.state) ? location.state : null;

  // When route state lacks user type, infer it from API data before rendering.
  const shouldResolveUserType = !state?.userType;
  const {
    data: userDetailsData,
    isFetching: isUserDetailsFetching,
  } = useGetUserDetailsQuery(shouldResolveUserType && id ? id : skipToken);
  const {
    data: rolesData,
    isFetching: isRolesFetching,
  } = useGetUserRolesQuery(
    shouldResolveUserType
      ? {
          is_active: true,
          is_detailed: true,
        }
      : skipToken
  );

  const roleName =
    rolesData?.items?.find(
      (role: { id?: number }) =>
        role?.id === userDetailsData?.user_role_id
    )?.name ?? "";
  const derivedUserType = resolveUserTypeFromRoleName(roleName);
  const resolvedUserType = state?.userType ?? derivedUserType;
  // Avoid rendering the wrong edit form while user type is still resolving.
  const isResolvingUserType =
    shouldResolveUserType &&
    !resolvedUserType &&
    (isUserDetailsFetching || isRolesFetching);

  if (isResolvingUserType) {
    return null;
  }
  const finalUserType: SupportedUserType =
    (resolvedUserType as SupportedUserType | undefined) ?? "NURSE";

  if (finalUserType === "ADMIN") {
    return <EditAdmin />;
  }

  if (finalUserType === "COURIER") {
    return <EditCourier />;
  }

  // Default to nurse edit form for NURSE and DOCTOR (non-courier, non-admin users).
  return <EditNurse />;
};

export default EditUser;
